import { ArrayBuffer as SparkArrayBuffer } from "spark-md5";

export const generateImage = async (
  originalImage: HTMLImageElement,
  name: string,
  cropData: {
    x: number
    y: number
    width: number
    height: number
  },
): Promise<File> => {
  try {
    const scaleX = originalImage.naturalWidth / originalImage.width;
    const scaleY = originalImage.naturalHeight / originalImage.height;

    const offscreen = new OffscreenCanvas(cropData.width * scaleX, cropData.height * scaleY);
    const context = offscreen.getContext("2d");
    if (!context) throw new Error("Impossible de créer le contexte 2D");

    context.drawImage(
      originalImage,
      cropData.x * scaleX,
      cropData.y * scaleY,
      cropData.width * scaleX,
      cropData.height * scaleY,
      0,
      0,
      offscreen.width,
      offscreen.height,
    );

    const blob = await offscreen.convertToBlob({ type: "image/png" });

    return new File([blob], `${name}.png`, { type: "image/png" });
  }
  catch {
    throw new Error("Nous n'avons pas réussi à générer votre image, veuillez réessayer");
  }
};

export const getFileFromUrl = async (url: string, name: string): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Failed to get canvas context"));
        return;
      }

      ctx.drawImage(img, 0, 0);

      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Failed to convert canvas to blob"));
          return;
        }
        try {
          resolve(new File([blob], `${name}.png`, { type: "image/png" }));
        }
        catch (error) {
          reject(error instanceof Error ? error : new Error("Failed to create file from blob"));
        }
      }, "image/png");
    };

    img.onerror = () => {
      reject(new Error("Failed to load image"));
    };

    img.src = url;
  });
};

export const computeMD5Base64 = async (file: File): Promise<string> => {
  const buffer = await file.arrayBuffer();
  const rawHash = SparkArrayBuffer.hash(buffer, true);
  return btoa(rawHash);
};

/**
 * Crop an image from a URL using coordinates in natural image pixels.
 * Used with react-easy-crop which provides croppedAreaPixels in natural coords.
 */
export const cropImageFromUrl = (
  imageSrc: string,
  name: string,
  cropArea: {
    x: number
    y: number
    width: number
    height: number
  },
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = async () => {
      try {
        const offscreen = new OffscreenCanvas(cropArea.width, cropArea.height);
        const ctx = offscreen.getContext("2d");
        if (!ctx) throw new Error("Impossible de créer le contexte 2D");

        ctx.drawImage(
          img,
          cropArea.x, cropArea.y, cropArea.width, cropArea.height,
          0, 0, cropArea.width, cropArea.height,
        );

        const blob = await offscreen.convertToBlob({ type: "image/png" });
        resolve(new File([blob], `${name}.png`, { type: "image/png" }));
      }
      catch {
        reject(new Error("Nous n'avons pas réussi à générer votre image, veuillez réessayer"));
      }
    };

    img.onerror = () => {
      reject(new Error("Impossible de charger l'image"));
    };

    img.src = imageSrc;
  });
};

const BLOB_URL_PREFIX = "blob:";

export const isObjectUrl = (url: string | null): boolean => {
  return url?.startsWith(BLOB_URL_PREFIX) ?? false;
};

export const revokeObjectUrl = (url: string | null): void => {
  if (isObjectUrl(url)) {
    URL.revokeObjectURL(url as string);
  }
};

const ROTATION_FULL_TURN = 360;

export const normalizeRotation = (degrees: number): number => {
  return ((degrees % ROTATION_FULL_TURN) + ROTATION_FULL_TURN) % ROTATION_FULL_TURN;
};

export const isImageMimeType = (mimeType: string | null | undefined): boolean =>
  Boolean(mimeType?.startsWith("image/"));

export const isImageFile = (file: File): boolean => isImageMimeType(file.type);

const loadImageFromSource = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Impossible de charger l'image"));
    img.src = src;
  });

const drawRotatedImageToCanvas = (
  img: HTMLImageElement,
  rotation: number,
): OffscreenCanvas => {
  const isQuarterTurn = rotation === 90 || rotation === 270;
  const width = isQuarterTurn ? img.naturalHeight : img.naturalWidth;
  const height = isQuarterTurn ? img.naturalWidth : img.naturalHeight;

  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Impossible de créer le contexte 2D");

  ctx.translate(width / 2, height / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);

  return canvas;
};

const inferOutputMimeType = (sourceMimeType: string): "image/jpeg" | "image/webp" | "image/png" => {
  if (sourceMimeType === "image/jpeg" || sourceMimeType === "image/jpg") return "image/jpeg";
  if (sourceMimeType === "image/webp") return "image/webp";
  return "image/png";
};

const replaceFileExtension = (filename: string, mimeType: string): string => {
  const extension = mimeType === "image/jpeg" ? "jpg" : mimeType === "image/webp" ? "webp" : "png";
  const dotIndex = filename.lastIndexOf(".");
  const base = dotIndex > 0 ? filename.slice(0, dotIndex) : filename;
  return `${base}.${extension}`;
};

const canvasToFile = async (
  canvas: OffscreenCanvas,
  filename: string,
  outputType: "image/jpeg" | "image/webp" | "image/png",
): Promise<File> => {
  // Quality 0.92 limits re-encoding loss when the user rotates an image multiple times.
  const blob = await canvas.convertToBlob(
    outputType === "image/jpeg" ? { type: outputType, quality: 0.92 } : { type: outputType },
  );
  return new File([blob], replaceFileExtension(filename, outputType), {
    type: outputType,
    lastModified: Date.now(),
  });
};

export const rotateImageFile = async (file: File, degrees: number): Promise<File> => {
  const rotation = normalizeRotation(degrees);
  if (rotation === 0) return file;
  if (!isImageFile(file)) throw new Error("Seuls les fichiers image peuvent être pivotés");

  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImageFromSource(objectUrl);
    const canvas = drawRotatedImageToCanvas(img, rotation);
    return await canvasToFile(canvas, file.name, inferOutputMimeType(file.type));
  }
  finally {
    URL.revokeObjectURL(objectUrl);
  }
};

export const rotateImageFromUrl = async (
  url: string,
  filename: string,
  degrees: number,
  sourceMimeType: string = "image/jpeg",
): Promise<File> => {
  const rotation = normalizeRotation(degrees);
  const img = await loadImageFromSource(url);
  const canvas = drawRotatedImageToCanvas(img, rotation);
  return canvasToFile(canvas, filename, inferOutputMimeType(sourceMimeType));
};
